import { useEffect, useState } from "react";
import useVirtualWorld from "../hooks/useVirtualWorld";
import vw from "../api/virtualWorld.api";
import Loading from "../components/Loading";
import { HighlightRanking } from "../components/VirtualWorld/MatchHighlights";

const money = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;

const tierClass = {
  bronze: "border-orange-300 bg-orange-50 dark:bg-orange-900/20",
  silver: "border-gray-300 bg-gray-50 dark:bg-gray-700",
  gold: "border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20",
};

function RankCard({ i, title, sub, score, meta, mine }) {
  return (
    <div className={`rounded-2xl border p-4 transition hover:-translate-y-1 hover:shadow-lg ${mine ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 ring-2 ring-emerald-300 animate-pulse" : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700"}`}>
      <div className="flex justify-between gap-3">
        <div>
          <div className="text-xs uppercase opacity-60">#{i + 1}</div>
          <div className="font-black text-lg">{title}</div>
          <div className="text-sm opacity-75">{sub}</div>
          {mine ? <div className="text-xs font-bold text-emerald-700 dark:text-emerald-300 mt-1">Dein Eintrag</div> : null}
        </div>
        <div className="text-right">
          <div className="text-2xl font-black">{score}</div>
          <div className="text-xs opacity-70">{meta}</div>
        </div>
      </div>
    </div>
  );
}

function BadgeGrid({ title, badges }) {
  return (
    <div className="bg-white dark:bg-gray-700 rounded-2xl p-5 border border-gray-200 dark:border-gray-600">
      <h2 className="text-2xl font-bold mb-3">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(badges || []).map((b) => (
          <div key={b.key} className={`rounded-xl border p-3 transition ${tierClass[b.tier] || tierClass.bronze} ${b.unlocked ? "hover:-translate-y-1 hover:shadow-lg" : "opacity-45 grayscale"}`}>
            <div className="font-bold">{b.unlocked ? "🏅" : "🔒"} {b.title}</div>
            <div className="text-sm opacity-75">{b.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const VirtualWorldHall = () => {
  const { userId, loading } = useVirtualWorld();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!userId) return;
    vw.hall(userId).then((res) => setData(res.data)).catch(() => setData(null));
  }, [userId]);

  if (loading || !data) return <Loading />;

  return (
    <div className="px-8 py-12 text-black dark:text-white space-y-8">
      <div className="bg-gray-900 text-white rounded-3xl p-8 border border-yellow-400">
        <div className="text-sm uppercase tracking-wide text-yellow-200 mb-2">Virtuelle Dartswelt</div>
        <h1 className="text-5xl font-black mb-3">🏛️ Ruhmeshalle</h1>
        <p className="opacity-80">
          Rankings und Achievements außerhalb der Spieltabellen: Manager-Prestige, Karriere-Ruhm,
          Badges und langfristige Ziele.
        </p>
        <p className="opacity-75 text-sm mt-2">
          Multiplayer-Logik: mehrere echte Manager spielen in derselben asynchronen Welt und vergleichen sich hier über Clubs,
          Spielerkarrieren, Transfers und virtuelle Resultate.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mt-6">
          <div className="rounded-xl bg-white/10 p-3"><div className="text-xs opacity-70">Clubs</div><div className="text-2xl font-black">{data.totals?.clubs || 0}</div></div>
          <div className="rounded-xl bg-white/10 p-3"><div className="text-xs opacity-70">Karrieren</div><div className="text-2xl font-black">{data.totals?.careers || 0}</div></div>
          <div className="rounded-xl bg-white/10 p-3"><div className="text-xs opacity-70">Virtuelle Spielergebnisse</div><div className="text-2xl font-black">{data.totals?.virtualMatches || 0}</div></div>
        </div>
      </div>

      <BadgeGrid title="🎖️ Meine Manager-Badges" badges={data.myManagerBadges} />
      <BadgeGrid title="🎯 Meine Karriere-Badges" badges={data.myCareerBadges} />

      <HighlightRanking rows={data.highlightRanking || []} title="Highlight-Rangliste (alle Events)" />

      <div className="grid xl:grid-cols-2 gap-6">
        <section>
          <h2 className="text-2xl font-bold mb-3">🏆 Club-Ranking</h2>
          <div className="space-y-3">
            {(data.managerRanking || []).map((row, i) => (
              <RankCard
                key={row.id}
                i={i}
                title={row.name}
                sub={`${row.level} · Regulär ${row.regular} · ${row.points} Pkt`}
                score={row.score}
                meta={`${money(row.budget)} Budget`}
                mine={row.mine}
              />
            ))}
          </div>
        </section>
        <section>
          <h2 className="text-2xl font-bold mb-3">⭐ Karriere-Ranking</h2>
          <div className="space-y-3">
            {(data.careerRanking || []).map((row, i) => (
              <RankCard
                key={row.id}
                i={i}
                title={row.username}
                sub={`${row.level} · ${row.clubName || row.role} · ${row.wins} Siege / ${row.matches} Spiele`}
                score={row.score}
                meta={`AVG ${row.avg || "-"} · 180er ${row.max || 0}`}
                mine={row.mine}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default VirtualWorldHall;
